import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  getProviderRegistrations, getProviderTrainings,
  createTrainingInvoice, CreateInvoiceData
} from '../services/opleidingenApi';
import { TrainingRegistration } from '../types';
import { berekenMatrix, MATRIX_KORTING_PCT, MATRIX_PLATFORMFEE_PCT } from '../utils/academyMatrix';
import { Users, FileText, Euro, X, Check } from 'lucide-react';

export const OpleiderRegistratiesPage: React.FC = () => {
  const { t } = useTranslation('pages');
  const queryClient = useQueryClient();
  const [filterTraining, setFilterTraining] = useState('');
  const [invoiceModal, setInvoiceModal] = useState<TrainingRegistration | null>(null);
  const [invoiceForm, setInvoiceForm] = useState({ bedrag: '', notities: '' });

  const { data: trainings = [] } = useQuery({
    queryKey: ['provider-trainings'],
    queryFn: getProviderTrainings,
  });

  const { data: registrations = [], isLoading } = useQuery({
    queryKey: ['provider-registrations', filterTraining],
    queryFn: () => getProviderRegistrations(filterTraining ? parseInt(filterTraining) : undefined),
  });

  const invoiceMutation = useMutation({
    mutationFn: (data: CreateInvoiceData) => createTrainingInvoice(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['provider-registrations'] });
      queryClient.invalidateQueries({ queryKey: ['provider-invoices'] });
      setInvoiceModal(null);
      setInvoiceForm({ bedrag: '', notities: '' });
    },
  });

  const openInvoiceModal = (reg: TrainingRegistration) => {
    setInvoiceModal(reg);
    setInvoiceForm({
      bedrag: reg.prijs_excl_btw ? String(reg.prijs_excl_btw) : '',
      notities: '',
    });
  };

  const handleSendInvoice = () => {
    if (!invoiceModal || !invoiceForm.bedrag) return;
    invoiceMutation.mutate({
      registration_id: invoiceModal.id,
      bedrag_excl_btw: parseFloat(invoiceForm.bedrag),
      notities: invoiceForm.notities || undefined,
    });
  };

  const STATUS_MAP: Record<string, { label: string; cls: string }> = {
    aangemeld: { label: t('opleider.statusAangemeld'), cls: 'bg-blue-100 text-blue-700' },
    bevestigd: { label: t('opleider.statusBevestigd'), cls: 'bg-green-100 text-green-700' },
    betaald: { label: t('opleider.statusBetaald'), cls: 'bg-emerald-100 text-emerald-700' },
    geannuleerd: { label: t('opleider.statusGeannuleerd'), cls: 'bg-gray-100 text-gray-400' },
  };

  return (
    <div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <div>
            <h1 className="text-xl font-bold text-gray-900">{t('opleider.registrationsTitle')}</h1>
            <p className="text-sm text-gray-500">{t('opleider.registrationsSubtitle')}</p>
          </div>
        </div>

        {/* Filter */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-5 flex items-center gap-3">
          <Users className="w-5 h-5 text-gray-400" />
          <select
            value={filterTraining}
            onChange={(e) => setFilterTraining(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
          >
            <option value="">{t('opleider.allTrainings')}</option>
            {trainings.map((t) => <option key={t.id} value={t.id}>{t.titel}</option>)}
          </select>
          <span className="text-sm text-gray-500 ml-auto">
            {registrations.length} {registrations.length !== 1 ? t('opleider.registrationPlural') : t('opleider.registrationSingular')}
          </span>
        </div>

        {/* Tabel */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {isLoading ? (
            <div className="p-10 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0B1F3A] mx-auto" />
            </div>
          ) : registrations.length === 0 ? (
            <div className="p-10 text-center">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">{t('opleider.noRegistrationsYet')}</p>
            </div>
          ) : (
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">{t('opleider.colProfessional')}</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">{t('opleider.colTraining')}</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">{t('opleider.colRegisteredOn')}</th>
                  <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">{t('opleider.colStatus')}</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {registrations.map((reg) => {
                  const { label, cls } = STATUS_MAP[reg.status] || { label: reg.status, cls: 'bg-gray-100 text-gray-500' };
                  const canInvoice = reg.status === 'aangemeld';
                  return (
                    <tr key={reg.id} className="hover:bg-gray-50">
                      <td className="px-5 py-4">
                        <div className="font-medium text-sm text-gray-900 flex items-center gap-2">
                          {reg.first_name} {reg.last_name}
                          {reg.bron === 'medewerker_academy' && (
                            <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#0B1F3A]/10 text-[#0B1F3A]" title={t('opleider.academyBadgeTitle')}>
                              {t('opleider.academyBadge')}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">{reg.professional_email}</div>
                        {reg.phone && <div className="text-xs text-gray-400">{reg.phone}</div>}
                        {reg.geboortedatum && (
                          <div className="text-xs text-gray-400">
                            {t('opleider.geboortedatum')}: {new Date(`${reg.geboortedatum}T00:00:00`).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}
                          </div>
                        )}
                        {reg.registratienummer && <div className="text-[11px] text-gray-400 font-mono">{reg.registratienummer}</div>}
                      </td>
                      <td className="px-5 py-4">
                        <div className="text-sm text-gray-700">{reg.training_titel}</div>
                        {reg.prijs_excl_btw != null && (
                          <div className="text-xs text-gray-400 flex items-center gap-0.5">
                            <Euro className="w-3 h-3" />{Number(reg.prijs_excl_btw).toFixed(2).replace('.', ',')}
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-500">
                        {new Date(reg.aangemeld_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </td>
                      <td className="px-5 py-4">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}>{label}</span>
                      </td>
                      <td className="px-5 py-4 text-right">
                        {canInvoice && (
                          <button
                            onClick={() => openInvoiceModal(reg)}
                            className="flex items-center gap-1.5 text-xs font-medium text-[#0B1F3A] hover:text-[#E5A425] transition-colors"
                          >
                            <FileText className="w-3.5 h-3.5" />
                            {t('opleider.sendInvoice')}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Factuur modal */}
      {invoiceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-900">{t('opleider.sendInvoiceTitle')}</h2>
              <button onClick={() => setInvoiceModal(null)} className="p-1 hover:bg-gray-100 rounded">
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm">
              <div className="font-medium text-gray-900">{invoiceModal.first_name} {invoiceModal.last_name}</div>
              <div className="text-gray-500">{invoiceModal.training_titel}</div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('opleider.amountExclVat')}</label>
                <input
                  type="number"
                  required
                  min="0"
                  step="0.01"
                  value={invoiceForm.bedrag}
                  onChange={(e) => setInvoiceForm((p) => ({ ...p, bedrag: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A]"
                />
                {invoiceForm.bedrag && (() => {
                  // Core-Matrix 5/10/85 — zelfde centen-rekenregel als de server. Een Professional
                  // Academy-activatie met BYOE-premium heeft 10% korting en 5% fee; de opleider houdt 85%.
                  const kortingPct =
                    invoiceModal?.bron === 'academy' && invoiceModal.korting_pct != null
                      ? Number(invoiceModal.korting_pct)
                      : MATRIX_KORTING_PCT;
                  const m = berekenMatrix(
                    parseFloat(invoiceForm.bedrag),
                    kortingPct,
                    MATRIX_KORTING_PCT + MATRIX_PLATFORMFEE_PCT - kortingPct
                  );
                  const fmt = (n: number) => n.toFixed(2).replace('.', ',');
                  return (
                    <div className="mt-1 text-xs text-gray-500 space-y-0.5">
                      <div>{t('opleider.factuurwaarde', { amount: fmt(m.activeringswaarde) })}</div>
                      <div>{t('opleider.inclVat', { amount: fmt(Math.round(m.activeringswaarde * 121) / 100) })}</div>
                      <div>{t('opleider.belagomCommission', { amount: fmt(m.platformfee_bedrag) })}</div>
                      <div className="font-medium text-green-700">{t('opleider.youReceive', { amount: fmt(m.opleider_netto) })}</div>
                    </div>
                  );
                })()}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('opleider.notesOptional')}</label>
                <textarea
                  value={invoiceForm.notities}
                  onChange={(e) => setInvoiceForm((p) => ({ ...p, notities: e.target.value }))}
                  rows={2}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B1F3A] resize-none"
                />
              </div>
            </div>

            {invoiceMutation.isError && (
              <p className="mt-3 text-sm text-red-600">
                {(invoiceMutation.error as any)?.response?.data?.message || t('opleider.invoiceError')}
              </p>
            )}

            <div className="flex gap-3 mt-5">
              <button
                onClick={handleSendInvoice}
                disabled={!invoiceForm.bedrag || invoiceMutation.isPending}
                className="flex items-center gap-2 flex-1 bg-[#0B1F3A] text-white py-2.5 rounded-lg font-medium hover:bg-[#1A3459] transition-colors disabled:opacity-60 justify-center"
              >
                <Check className="w-4 h-4" />
                {invoiceMutation.isPending ? t('opleider.sending') : t('opleider.sendInvoiceBtn')}
              </button>
              <button
                onClick={() => setInvoiceModal(null)}
                className="px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50"
              >
                {t('opleider.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OpleiderRegistratiesPage;
